/* @flow */
import * as React from 'react';
import { observer, inject } from 'mobx-react';
import { reaction, computed } from 'mobx';
import { translate } from 'react-i18next';
import { Link } from 'react-router-dom';
import moment from 'moment';
import type { TranslatorProps } from 'react-i18next';
import ReactPlaceholder from 'react-placeholder';

import { isIE } from 'utils/browserDetection';
import Loading from 'components/Loading/Loading';
import navigation from 'services/NavigationService';
import DashboardStore from 'stores/DashboardStore';
import { ComponentWithInjectedProps } from 'utils/injectedProps';

import NivoBarChart from '../../components/NivoBarChart';
import type { CommonWidgetProps } from '../../commonTypes';

import styles from './AssetsByUser.module.scss';
import NavigationService from '../../../../services/NavigationService';

const STATIC_DATA = [];

export type AssetsByUserProps = CommonWidgetProps;

type InjectedProps = {
  dashboardStore: DashboardStore,
} & TranslatorProps;

@translate()
@inject('dashboardStore')
@observer
export default class AssetsByUser extends ComponentWithInjectedProps<
  AssetsByUserProps,
  {},
  InjectedProps
> {
  static LOAD_THUMBNAIL = false;

  @computed get shouldLoadData(): boolean {
    const { size } = this.props;

    return size !== 'thumbnail' || AssetsByUser.LOAD_THUMBNAIL;
  }

  @computed get widgetData(): any[] | null {
    const { dashboardStore } = this.props;

    if (!this.shouldLoadData) {
      return STATIC_DATA;
    }

    return dashboardStore.assetsByUser ? dashboardStore.assetsByUser['assets-user'] : null;
  }

  autoUpdateDisposer: Function;

  componentDidMount() {
    const { dashboardStore } = this.props;

    this.loadWidgetData(dashboardStore.loadedTradingAccount);

    this.autoUpdateDisposer = reaction(
      () => dashboardStore.loadedTradingAccount,
      ta => {
        this.loadWidgetData(ta);
      }
    );
  }

  loadWidgetData = (tradingAccount: string) => {
    const { dashboardStore } = this.props;

    if (this.shouldLoadData) {
      dashboardStore.loadAssetsByUser(tradingAccount, 10);
    }
  };

  componentWillUnmount() {
    this.autoUpdateDisposer();
  }

  render() {
    const { dashboardStore, t, widgetItem, size, isFullScreen } = this.props;
    const accountId = dashboardStore.loadedTradingAccount;

    const allAssets = [];
    const allOverdue = [];

    const widgetTitle =
      size === 'thumbnail'
        ? t('general:dashboards.assets_by_user_thumb')
        : t('general:dashboards.assets_by_user');

    const userData = this.widgetData
      ? this.widgetData
          .map((asset: any, index: number) => {
            if (asset.not_expiring === 0 && asset.expiring === 0) {
              return null;
            }

            allAssets.push(asset.expiring);
            allAssets.push(asset.not_expiring);
            allOverdue.push(asset.expiring);

            return {
              user: asset.user_name,
              user_id: asset.user_id,
              'Rented assets expiring in 30+ days': asset.not_expiring,
              'Rented assets expiring in 30+ daysColor': 'HSL(198, 13%, 34%)',
              'Rented assets expiring in under 30 days': asset.expiring,
              'Rented assets expiring in under 30 daysColor': 'HSL(12, 74%, 59%)',
            };
          })
          .filter(obj => obj !== null)
      : null;

    const over30days: string = `${moment(new Date())
      .add(30, 'days')
      .format('YYYY-MM-DD')}--`;

    const under30days: string = `${moment(new Date()).format('YYYY-MM-DD')}--${moment(new Date())
      .add(30, 'days')
      .format('YYYY-MM-DD')}`;

    function expireDateRange(id) {
      return id.match(/[+]/g) ? over30days : under30days;
    }

    function add(total: number, num: number) {
      return total + num;
    }

    const AssetsByUserThumbnail = (
      <div className={styles['thumbnail-container']}>
        <div className={styles['full-container']}>
          {this.shouldLoadData && dashboardStore.loadingAssetsByUser ? (
            <Loading />
          ) : (
            <React.Fragment>
              <div className={styles['half-container']}>
                <ReactPlaceholder
                  ready={this.shouldLoadData}
                  type="textRow"
                  style={{ marginBottom: 20, width: '50%' }}
                  color="rgba(255, 255, 255, 0.2)"
                >
                  <span>{allAssets.length ? allAssets.reduce(add) : 0}</span>
                </ReactPlaceholder>
                <small>
                  {t('general:assets.labels.expiring_over', {
                    title: '$t(general:assets.rented_asset)',
                    count: allOverdue.length,
                  })}
                </small>
              </div>
              <div className={styles['half-container']}>
                <ReactPlaceholder
                  ready={this.shouldLoadData}
                  type="textRow"
                  style={{ marginBottom: 20, width: '50%' }}
                  color="rgba(255, 255, 255, 0.2)"
                >
                  <span>{allOverdue.length ? allOverdue.reduce(add) : 0}</span>
                </ReactPlaceholder>
                <small>
                  {t('general:assets.labels.expiring_under', {
                    title: '$t(general:assets.rented_asset)',
                    count: allOverdue.length,
                  })}
                </small>
              </div>
            </React.Fragment>
          )}
        </div>
      </div>
    );

    return (
      <NivoBarChart
        widgetInfos={[{ title: widgetTitle, variant: 'default' }]}
        tableTitles={[t('general:user'), t('general:dashboards.abbrs.number_of_assets')]}
        widgetItem={widgetItem}
        size={size}
        isFullScreen={isFullScreen}
        isIE={isIE}
        data={userData}
        isLoading={this.shouldLoadData && dashboardStore.loadingAssetsByUser}
        legendKeys={[
          {
            label: t('general:assets.labels.key_expiring_over'),
            color: 'hsl(198, 13%, 34%)',
          },
          {
            label: t('general:assets.labels.key_expiring_under'),
            color: 'hsl(12, 74%, 59%)',
          },
        ]}
        button={Boolean(accountId.length > 0)}
        buttonLink={{
          pathname: `/accounts/${accountId}`,
          state: { tab: 1 },
        }}
        buttonText={t('general:actions.view_user_plural')}
        thumbnail={AssetsByUserThumbnail}
        // Nivo bar chart props
        indexBy="user_id"
        keys={['Rented assets expiring in 30+ days', 'Rented assets expiring in under 30 days']}
        colorBy={function(e: any): string {
          return e.data[e.id + 'Color'];
        }}
        axisLeft={{
          renderTick: tick => {
            const username: string = userData[tick.tickIndex].user;
            const abbrvName: string =
              username.length > 16 ? username.substring(0, 16) + '...' : username;
            const link = NavigationService.formulateReportViewUri('/assets', {
              page: 1,
              filters: [
                ...(accountId ? [{ key: 'account', values: [accountId] }] : []),
                { key: 'user_id', values: [tick.key] },
              ],
            });

            return (
              <g key={tick.key} transform={`translate(${tick.x},${tick.y})`}>
                <text
                  className={styles['user-label']}
                  textAnchor="end"
                  alignmentBaseline="central"
                  transform="translate(-12, 0)"
                >
                  <Link to={link}>{abbrvName}</Link>
                </text>
              </g>
            );
          },
        }}
        onClick={e => {
          navigation.navigateTo(`/assets/user/${e.data.user_id}/end-date/${expireDateRange(e.id)}`);
        }}
        tooltip={function(data) {
          return (
            <div>
              {data.id}: <b>{data.value}</b>
            </div>
          );
        }}
      />
    );
  }
}
